import { useState } from 'react';
import Button from "@/components/Button";
import {
  ChevronDown,
  ChevronUp,
  ChevronLeft,
  ChevronRight
} from "lucide-react";
import departments from "@/data/departments";

// Mover la lógica de paginación y estado local si es necesario, o asumir que se pasa como props.
// En este caso, el estado local (expandedDepartment, currentPage) y sus setters
// se manejan en el componente padre (la página), así que los recibimos como props.

export function TeamSection({ expandedDepartment, setExpandedDepartment, currentPage, setCurrentPage, className }) {
  const membersPerPage = 12;

  const changePage = (deptName, delta) => {
    const department = departments.find((d) => d.name === deptName);
    if (!department) return;

    const totalPages = Math.ceil(department.members.length / membersPerPage);
    setCurrentPage((prev) => {
      const current = prev[deptName] || 0;
      const newPage = Math.max(0, Math.min(current + delta, totalPages - 1));
      return { ...prev, [deptName]: newPage };
    });
  };

  return (
    // La clase de fondo principal de la sección puede ser pasada por props (className)
    <section className={`w-full py-12 md:py-24 lg:py-32 ${className || 'bg-white'}`}> 
      <div className="container mx-auto px-4 md:px-6">
        <h2 className="text-3xl font-bold tracking-tighter text-blue-900 sm:text-4xl md:text-5xl text-center mb-8">
          Nuestro Equipo
        </h2>
        <div className="space-y-4">
          {departments.map((dept) => (
            <Department
              key={dept.name}
              department={dept}
              expandedDepartment={expandedDepartment}
              setExpandedDepartment={setExpandedDepartment}
              currentPage={currentPage}
              changePage={changePage}
            />
          ))}
        </div>
      </div>
    </section>
  );
}

function Department({ department, expandedDepartment, setExpandedDepartment, currentPage, changePage }) {
  const membersPerPage = 12;
  const currentDeptPage = currentPage[department.name] || 0;

  const startIndex = currentDeptPage * membersPerPage;
  const endIndex = startIndex + membersPerPage;
  const membersToShow = department.members.slice(startIndex, endIndex);

  const totalPages = Math.ceil(department.members.length / membersPerPage);

  return (
    <div className="border border-blue-700 rounded-lg overflow-hidden">
      <button
        className="w-full p-4 flex justify-between items-center bg-blue-800 hover:bg-blue-700 transition-colors"
        onClick={() => setExpandedDepartment(expandedDepartment === department.name ? null : department.name)}
        aria-expanded={expandedDepartment === department.name}
      >
        <span className="text-xl font-bold text-white">{department.name}</span>
        {expandedDepartment === department.name ? (
          <ChevronUp className="h-6 w-6 text-white" />
        ) : (
          <ChevronDown className="h-6 w-6 text-white" />
        )}
      </button>
      {expandedDepartment === department.name && (
        <div className="p-4 bg-blue-100"> 
          <div className="grid gap-4 grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-4">
            {membersToShow.map((member) => (
              <Member key={member.name} member={member} />
            ))}
          </div>
          {/* Mejorar el diseño de paginación para móvil */}
          {totalPages > 1 && (
            <div className="mt-6 space-y-3">
              {/* Indicador de página centrado */}
              <div className="text-center">
                <span className="text-sm font-medium text-blue-800 bg-blue-50 px-3 py-1 rounded-full">
                  Página {currentDeptPage + 1} de {totalPages}
                </span>
              </div>
              {/* Botones de navegación */}
              <div className="flex justify-center gap-3 px-2">
                <Button 
                  onClick={() => changePage(department.name, -1)} 
                  disabled={currentDeptPage === 0}
                  variant="default" 
                  className="flex items-center gap-1 text-sm px-3 py-2 min-w-[100px] justify-center"
                >
                  <ChevronLeft className="w-4 h-4" />
                  <span className="hidden xs:inline">Anterior</span>
                  <span className="xs:hidden">Ant.</span>
                </Button>
                <Button
                  onClick={() => changePage(department.name, 1)}
                  disabled={currentDeptPage === totalPages - 1}
                  variant="default" 
                  className="flex items-center gap-1 text-sm px-3 py-2 min-w-[100px] justify-center"
                >
                  <span className="hidden xs:inline">Siguiente</span>
                  <span className="xs:hidden">Sig.</span>
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function Member({ member }) {
  return (
    <div className="flex flex-col items-center text-center">
      <div className="w-28 h-28 sm:w-32 sm:h-32 md:w-36 md:h-36 mb-3 overflow-hidden rounded-full border-2 border-blue-200"> {/* Imágenes más grandes */}
        <img
          src={member.image || '/images/user_default.png'} /* Usar imagen por defecto consistente */
          alt={member.name}
          className="object-cover w-full h-full"
          onError={(e) => { e.target.onerror = null; e.target.src='/images/user_default.png'; }} /* Fallback por si la imagen no carga */
        />
      </div>
      <h3 className="text-sm sm:text-lg font-bold text-blue-800 leading-tight">{member.name}</h3>
      <p className="text-xs sm:text-sm text-blue-700">{member.role}</p>
    </div>
  );
} 